import type { LocalChartRepository } from './localPersistence.js';
import { savedChartDisplayViewModel, type SavedChartDisplayViewModel } from './savedChartPresentation.js';
import { executeProductionDeleteAction, type ProductionDeleteResult } from './productionDeleteCommand.js';
import { buildProductionChartExport, type ProductionExportActionId, type ProductionExportFile } from './productionExportCommand.js';
import {
  executeProductionLocalChartImport,
  preflightProductionLocalChartImport,
  type ProductionLocalImportPreflight,
} from './productionLocalImport.js';
import type { LocalChartRestoreResult } from './localChartTransfer.js';
import type { PrivacyDataManagementViewModel } from './privacyDataManagementPresentation.js';
import { buildPrivacyDataManagementViewModel } from './privacyDataManagementPresentation.js';
import type { ChronologySettingsViewModel } from './chronology.js';

export interface ManagedSavedChartViewModel extends SavedChartDisplayViewModel {
  handle: string;
}

export interface DataManagementControllerSnapshot {
  model: 'PRODUCTION-DATA-MANAGEMENT-CONTROLLER-0.1.0';
  charts: readonly ManagedSavedChartViewModel[];
  privacy: PrivacyDataManagementViewModel;
}

export class ProductionDataManagementController {
  readonly #repository: LocalChartRepository;
  readonly #handleToId = new Map<string, string>();
  readonly #idToHandle = new Map<string, string>();
  #nextHandle = 1;

  constructor(repository: LocalChartRepository) {
    this.#repository = repository;
  }

  snapshot(options: Readonly<{ revealBirthDetails?: boolean; chronology?: ChronologySettingsViewModel }> = {}): DataManagementControllerSnapshot {
    const charts = this.#repository.list().map((summary): ManagedSavedChartViewModel => {
      const handle = this.#handleForId(summary.id);
      return Object.freeze({ handle, ...savedChartDisplayViewModel(summary, options.revealBirthDetails === true) });
    });
    this.#dropStaleMappings(new Set(this.#repository.list().map((summary) => summary.id)));
    return Object.freeze({
      model: 'PRODUCTION-DATA-MANAGEMENT-CONTROLLER-0.1.0' as const,
      charts: Object.freeze(charts),
      privacy: buildPrivacyDataManagementViewModel(this.#repository, options.chronology),
    });
  }

  deleteSavedChart(handle: string, confirmed: boolean): ProductionDeleteResult {
    const id = this.#resolveHandle(handle);
    const result = executeProductionDeleteAction('delete_saved_chart', this.#repository, { confirmed, chartId: id });
    if (confirmed) {
      this.#handleToId.delete(handle);
      this.#idToHandle.delete(id);
    }
    return result;
  }

  clearAllLocalCharts(confirmed: boolean): ProductionDeleteResult {
    const result = executeProductionDeleteAction('clear_all_local_charts', this.#repository, { confirmed });
    if (confirmed) {
      this.#handleToId.clear();
      this.#idToHandle.clear();
    }
    return result;
  }

  buildExport(
    action: ProductionExportActionId,
    exportedAt: string,
    options: Readonly<{ confirmedSensitiveExport?: boolean }> = {},
  ): ProductionExportFile {
    return buildProductionChartExport(action, this.#repository, exportedAt, options);
  }

  preflightImport(input: string | unknown): ProductionLocalImportPreflight {
    return preflightProductionLocalChartImport(input);
  }

  importPrivateBackup(input: string | unknown, confirmed: boolean): LocalChartRestoreResult {
    const result = executeProductionLocalChartImport(this.#repository, input, confirmed);
    return result;
  }

  #handleForId(id: string): string {
    const existing = this.#idToHandle.get(id);
    if (existing) return existing;
    const handle = `saved-chart-${this.#nextHandle}`;
    this.#nextHandle += 1;
    this.#idToHandle.set(id, handle);
    this.#handleToId.set(handle, id);
    return handle;
  }

  #resolveHandle(handle: string): string {
    const normalized = handle.trim();
    if (!normalized) throw new Error('Saved chart handle is required');
    const id = this.#handleToId.get(normalized);
    if (!id) throw new Error('Saved chart handle is unknown or expired');
    if (!this.#repository.get(id)) throw new Error('Saved chart handle no longer points to a valid saved chart');
    return id;
  }

  #dropStaleMappings(currentIds: ReadonlySet<string>): void {
    for (const [id, handle] of this.#idToHandle.entries()) {
      if (currentIds.has(id)) continue;
      this.#idToHandle.delete(id);
      this.#handleToId.delete(handle);
    }
  }
}
